import React, { useState, useMemo, useEffect } from 'react';

import { countries as nationalitiesJSON } from 'data';
import {
  DateInputWithConfirm,
  InputWithConfirm,
  AutocompleteInputWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  getStringFromDate,
  getDateFromString,
  ContinueButton,
  isValidDate,
} from 'shared-components';

import { VQ } from '../../../../../store/student/types';
import { IChildDetails, IDate } from '../../../../../types';

import { isPersonTravellingWith } from '../../../../../utilities/pages';
import { useTranslation } from "react-i18next";

interface IProps {
  index: number;

  nationality: string;
  familyName: string;
  givenName: string;
  dob: string;
  gender: string;
  sameNationality?: boolean;
  ukVisa?: boolean;
  hasPassport?: boolean;
  passportNumber: string;
  placeOfBirth: string;
  travellingWith: string;

  onSaveAndClose: (details: IChildDetails) => void;
}

const ChildrenDetails = (props: IProps) => {
    const { t } = useTranslation('common');
  const { index, onSaveAndClose } = props;
  const [nationality, setNationality] = useState(props.nationality);
  const [familyName, setFamilyName] = useState(props.familyName);
  const [givenName, setGivenName] = useState(props.givenName);
  const [dDob, setDDob] = useState(() => getDateFromString(props.dob));
  const [dDobError, setDDobError] = useState('');
  const [sameNationality, setSameNationality] = useState(props.sameNationality);
  const [ukVisa, setUkVisa] = useState(props.ukVisa);
  const [hasPassport, setHasPassport] = useState(props.hasPassport);
  const [passportNumber, setPassportNumber] = useState(props.passportNumber);
  const [placeOfBirth, setPlaceOfBirth] = useState(props.placeOfBirth);
  const [travellingWith, setTravellingWith] = useState(props.travellingWith);
  const [gender, setGender] = useState(props.gender);

  const nationalities = useMemo(() => [...new Set(nationalitiesJSON.map(n => n.nationality))], []);
  const [nationalitySuggestions, setNationalitySuggestions] = useState(nationalities);

  const saveAndClose = () => {
    onSaveAndClose({
      nationality,
      gender,
      familyName,
      givenName,
      dob: getStringFromDate(dDob),
      sameNationality,
      ukVisa,
      hasPassport,
      passportNumber,
      placeOfBirth,
      travellingWith,
    });
  };

  const handleOnSaveAndCloseClick = (_event: React.MouseEvent<HTMLButtonElement>) => {
    saveAndClose();
  };

  const validateDate = (dateValue: IDate): string => {
    const { YYYY, DD, MM } = dateValue;

    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        return "Doesn't look like a valid date";
      }
      return '';
    }

    return '';
  };

  const onNationalityChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = nationalities.filter(s => s.toLowerCase().includes(value.toLowerCase()));
    setNationality(value);
    setNationalitySuggestions(suggestions);
  };

  const onBlurNationality = (evt: any) => {
    const value = (evt.currentTarget.value || '').trim();
    const nationality = nationalities.includes(value) ? value : '';
    setNationality(nationality);
    setNationalitySuggestions(nationalities);
  };

  const complete =
    nationality !== '' &&
    sameNationality !== undefined &&
    familyName !== '' &&
    givenName !== '' &&
    gender !== '' &&
    dDob.YYYY !== null &&
    dDob.MM !== null &&
    dDob.DD !== null &&
    dDobError === '' &&
    placeOfBirth !== '' &&
    travellingWith !== '' &&
    (isPersonTravellingWith(travellingWith)
      ? ukVisa !== undefined && hasPassport !== undefined && (hasPassport === true ? passportNumber !== '' : true)
      : true);

  useEffect(() => {
    if (!isPersonTravellingWith(travellingWith)) {
      setUkVisa(undefined);
      setHasPassport(undefined);
      setPassportNumber('');
    }
  }, [travellingWith]);

  useEffect(() => {
    if (hasPassport !== true) {
      setPassportNumber('');
    }
  }, [hasPassport]);

  return (
    <>
      <AutocompleteInputWithConfirm
        key={`${VQ.NATIONALITY}-${index}`}
        id={`${VQ.NATIONALITY}-${index}`}
        value={nationality}
        label={t('common:nationality')}
        onChange={(evt: any) => onNationalityChange(evt)}
        suggestions={nationalitySuggestions}
        onBlur={(evt: any) => onBlurNationality(evt)}
      />

      <RadioOptionsWithConfirm
        key={`${VQ.SAME_NATIONALITY}-${index}`}
        id={`${VQ.SAME_NATIONALITY}-${index}`}
        label={t('common:haveTheyAlwaysHadTheSameNational')}
        onClick={(question: IQuestion) => setSameNationality(question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: sameNationality === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: sameNationality === false,
          },
        ]}
      />

      <RadioOptionsWithConfirm
        key={`${VQ.GENDER}-${index}`}
        id={`${VQ.GENDER}-${index}`}
        label={t('common:gender')}
        onClick={(question: IQuestion) => setGender(question.label)}
        isInline={false}
        questions={[
          {
            char: 'F',
            label: 'Female',
            selected: gender === 'Female',
          },
          {
            char: 'M',
            label: 'Male',
            selected: gender === 'Male',
          },
          {
            char: 'O',
            label: 'Other',
            selected: gender === 'Other',
          },
        ]}
      />

      <InputWithConfirm
        key={`${VQ.FAMILY_NAME}-${index}`}
        id={`${VQ.FAMILY_NAME}-${index}`}
        value={familyName}
        label={t('common:familyName2')}
        onChange={(evt: any) => {
          setFamilyName(evt.currentTarget.value);
        }}
      />

      <InputWithConfirm
        key={`${VQ.GIVEN_NAME}-${index}`}
        id={`${VQ.GIVEN_NAME}-${index}`}
        value={givenName}
        label={t('common:givenName')}
        onChange={(evt: any) => {
          setGivenName(evt.currentTarget.value);
        }}
      />

      <DateInputWithConfirm
        key={`${VQ.DOB}-${index}`}
        id={`${VQ.DOB}-${index}`}
        label={t('common:dateOfBirth2')}
        value={dDob}
        errorText={dDobError}
        onChangeDD={(evt: any) => setDDob({ ...dDob, DD: evt.target.value })}
        onChangeMM={(evt: any) => setDDob({ ...dDob, MM: evt.target.value })}
        onChangeYY={(evt: any) => setDDob({ ...dDob, YYYY: evt.target.value })}
        onBlur={(_evt: any) => setDDobError(validateDate(dDob))}
      />

      <InputWithConfirm
        key={`${VQ.PLACE_OF_BIRTH}-${index}`}
        id={`${VQ.PLACE_OF_BIRTH}-${index}`}
        value={placeOfBirth}
        label={t('common:placeOfBirth')}
        onChange={(evt: any) => {
          setPlaceOfBirth(evt.currentTarget.value);
        }}
      />

      <RadioOptionsWithConfirm
        key={`${VQ.TRAVELLING_WITH}-${index}`}
        id={`${VQ.TRAVELLING_WITH}-${index}`}
        label={t('common:isThisPersonTravellingWithYou')}
        onClick={(question: IQuestion) => {
          setTravellingWith(question.label);
        }}
        questions={[
          {
            char: '1',
            label: 'Yes - under 1 month',
            selected: travellingWith === 'Yes - under 1 month',
          },
          {
            char: '2',
            label: 'Yes - over 1 month',
            selected: travellingWith === 'Yes - over 1 month',
          },
          {
            char: '3',
            label: 'No - not travelling',
            selected: travellingWith === 'No - not travelling',
          },
        ]}
      />
      {isPersonTravellingWith(travellingWith) ? (
        <>
          <RadioOptionsWithConfirm
            key={`${VQ.HAS_UK_VISA}-${index}`}
            id={`${VQ.HAS_UK_VISA}-${index}`}
            label={t('common:doTheyHaveAVisaForTheUk')}
            onClick={(question: IQuestion) => setUkVisa(question.label === 'Yes')}
            onKeyDown={(evt: any, question: IQuestion) => {
              switch (evt.key.toLowerCase()) {
                case 'y':
                  setUkVisa(true);
                  break;
                case 'n':
                  setUkVisa(false);
                  break;
                case 'enter':
                case 'space':
                  setUkVisa(question.char === 'Y');
                  break;
              }
            }}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: ukVisa === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: ukVisa === false,
              },
            ]}
          />

          <RadioOptionsWithConfirm
            key={`${VQ.HAS_PASSPORT}-${index}`}
            id={`${VQ.HAS_PASSPORT}-${index}`}
            label={t('common:doTheyHaveAPassportOrTravelDocum')}
            onClick={(question: IQuestion) => setHasPassport(question.label === 'Yes')}
            onKeyDown={(evt: any, question: IQuestion) => {
              switch (evt.key.toLowerCase()) {
                case 'y':
                  setHasPassport(true);
                  break;
                case 'n':
                  setHasPassport(false);
                  break;
                case 'enter':
                case 'space':
                  setHasPassport(question.char === 'Y');
                  break;
              }
            }}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasPassport === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: hasPassport === false,
              },
            ]}
          />

          {hasPassport ? (
            <InputWithConfirm
              key={`${VQ.PASSPORT_NUMBER}-${index}`}
              id={`${VQ.PASSPORT_NUMBER}-${index}`}
              value={passportNumber}
              label={t('common:currentPassportOrTravelDocumentN')}
              onChange={(evt: any) => setPassportNumber(evt.currentTarget.value)}
            />
          ) : null}
        </>
      ) : null}

      <ContinueButton index={index} complete={complete} handleOnSaveAndCloseClick={handleOnSaveAndCloseClick} />
    </>
  );
};

export default ChildrenDetails;
